import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { operatorAPI } from '../../lib/api';
import { 
  ArrowLeft, RefreshCw, FileText, Calendar, CheckCircle, XCircle,
  Search, MessageSquare, Clock, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Operator Reliability Dashboard
 * Shows recent school_cards and daily_plans for monitoring pipeline health.
 * 
 * Endpoints used:
 * - GET /api/operator/reliability/cards
 * - GET /api/operator/reliability/plans
 */
export const OperatorReliability = () => {
  const [cards, setCards] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [familyFilter, setFamilyFilter] = useState('');
  const [activeTab, setActiveTab] = useState('plans');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cardsRes, plansRes] = await Promise.all([
        operatorAPI.getRecentCards(20, familyFilter || null).catch(e => ({ data: { cards: [] } })),
        operatorAPI.getRecentPlans(20, familyFilter || null).catch(e => ({ data: { plans: [] } })),
      ]);
      
      setCards(cardsRes.data?.cards || []);
      setPlans(plansRes.data?.plans || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadData();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const cardTypeColor = (type) => {
    const colors = {
      'daily_class_update': 'bg-blue-100 text-blue-700',
      'event_logistics': 'bg-purple-100 text-purple-700',
      'consent_form': 'bg-red-100 text-red-700',
      'attendance_alert': 'bg-amber-100 text-amber-700',
      'transport_compliance': 'bg-green-100 text-green-700',
      'general_announcement': 'bg-slate-100 text-slate-700'
    };
    return colors[type] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div data-testid="operator-reliability-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/operator" className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Reliability Dashboard</h1>
            <p className="text-sm text-slate-500">Monitor school cards and daily plans pipeline</p>
          </div>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={familyFilter}
              onChange={(e) => setFamilyFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Filter by family name..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              data-testid="family-filter-input"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Search
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('plans')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'plans' 
              ? 'bg-primary text-white' 
              : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
          }`}
          data-testid="tab-plans"
        >
          <Calendar className="w-4 h-4 inline mr-2" />
          Daily Plans ({plans.length})
        </button>
        <button
          onClick={() => setActiveTab('cards')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'cards' 
              ? 'bg-primary text-white' 
              : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
          }`}
          data-testid="tab-cards"
        >
          <FileText className="w-4 h-4 inline mr-2" />
          School Cards ({cards.length})
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* Daily Plans Table */}
          {activeTab === 'plans' && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="plans-table">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Family</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Plan Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Child</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Sent</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Twilio SID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {plans.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                        No daily plans found
                      </td>
                    </tr>
                  ) : (
                    plans.map((plan) => (
                      <tr key={plan.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <Link 
                            to={`/operator/family/${plan.family_id}`}
                            className="text-primary hover:underline font-medium"
                          >
                            {plan.family_name}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700 font-mono">
                          {plan.plan_date}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {plan.child_name || '-'}
                        </td>
                        <td className="px-4 py-3">
                          {plan.sent_to_nanny ? (
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-xs">{formatDate(plan.sent_at)}</span>
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-slate-400">
                              <Clock className="w-4 h-4" />
                              <span className="text-xs">Pending</span>
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {plan.twilio_sid ? (
                            <span className="font-mono text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                              {plan.twilio_sid.slice(0, 12)}...
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {plan.error ? (
                            <span className="flex items-center gap-1 text-red-600 text-xs" title={plan.error}>
                              <AlertCircle className="w-4 h-4" />
                              FAILED
                            </span>
                          ) : plan.delivery_status ? (
                            <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                              plan.delivery_status === 'delivered' ? 'bg-green-100 text-green-700' :
                              plan.delivery_status === 'sent' ? 'bg-yellow-100 text-yellow-800' :
                              plan.delivery_status === 'attempted' ? 'bg-blue-100 text-blue-700' :
                              plan.delivery_status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {plan.delivery_status.toUpperCase()}
                            </span>
                          ) : plan.sent_to_nanny ? (
                            <span className="text-xs px-2 py-0.5 rounded font-medium bg-yellow-100 text-yellow-800">
                              SENT
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                              GENERATED
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {formatDate(plan.created_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* School Cards Table */}
          {activeTab === 'cards' && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="cards-table">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Family</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Child</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {cards.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                        No school cards found
                      </td>
                    </tr>
                  ) : (
                    cards.map((card) => (
                      <tr key={card.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <Link 
                            to={`/operator/family/${card.family_id}`}
                            className="text-primary hover:underline font-medium"
                          >
                            {card.family_name}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${cardTypeColor(card.card_type)}`}>
                            {card.card_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700 max-w-xs truncate" title={card.title}>
                          {card.title}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {card.child_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {formatDate(card.created_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OperatorReliability;
